// src/oura/screens/SiteAudit.tsx
//
// The staff Site screen (view "setup" in the business drill-in; the merchant's
// "Your setup" shares the view name and is a different screen). Four answers
// first: Site Health, AI Readability, speed on a phone and the crawl's issues.
// Then Core Web Vitals, what the crawl found (issues and the pages that
// matter), what a machine can read, Health by area, and the strengths and
// weaknesses. Every finding opens the same How to fix drawer.
//
// The cards themselves live in components/site; this screen holds the state
// they share: the reviewer's out-of-scope pages, the plan join, the `?issue=`
// deep link, the open drawer, the device on show, and the three refresh jobs
// (crawl, recompute, readability) that moved here with their cards from the
// Overview, on the same storage keys so a job started there resumes here.
//
// Behind Delivery and the Docs write switch (Phase 3, H38): the AI readers
// card, one line per reader, which A3's AI readers item closes on.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "@hook/use-snackbar";
import type { Business, ScoreSnapshot } from "../contract";
import type { View } from "../nav";
import { getAudit, type BusinessAudit, type PageSpeedRun } from "../audit";
import { IssueFixDrawer } from "../components/IssueFixDrawer";
import { CardRefresh, RefreshButton, RefreshConfirmDialog, RefreshProgress } from "../components/RefreshControl";
import { useRefreshJob, useStageRefresh } from "../hooks/useRefreshJob";
import { ANALYSE_REFRESH, CRAWL_REFRESH, STAGE_RERUN_LIVE } from "../refreshJobs";
import { fetchStoredRoles, loadStoredRoles } from "../pageRoles";
import { useOuraRole } from "../role";
import { cn } from "../lib/cn";
import { DELIVERY_ON } from "../delivery/flag";
import {
    getReadabilityRefresh,
    getTrackerSummary,
    startReadabilityRefresh,
    type AeoReadabilityBlock,
    type AeoSearchPerformance,
    type AeoTrackerSummaryTask,
} from "@service/aeo";
import { Bento, FoldScope, PageSentence, REDUCED_MOTION, Span2, useMediaQuery } from "@oura/ui";
import { CoreWebVitalsCard } from "../components/site/CoreWebVitalsCard";
import { HealthByAreaCard } from "../components/site/HealthByAreaCard";
import { MachineReadCard } from "../components/site/MachineReadCard";
import { SiteIssuesCard } from "../components/site/SiteIssuesCard";
import { SiteTiles } from "../components/site/SiteTiles";
import { StrengthsWeaknessesCard } from "../components/site/StrengthsWeaknessesCard";
import { AiReadersCard } from "../components/site/AiReadersCard";
import { DELIVERY_WORK_ON } from "../delivery/workFlag";
import { defaultStrategy } from "../components/site/pageSpeed";
import { READABILITY_ABSENT_TEXT, READABILITY_ROWS, SITE_HEALTH_ABSENT_TEXT, dayMonth } from "../components/site/siteCopy";
import { activeIssues, pagesWith, rolesByKey, scopeIssues, setAsideKeys } from "../components/site/siteIssues";
import { sitePageRows } from "../components/site/sitePages";

/** Search Console's window when the scan does not state one (fixtures): the
 *  backend pulls the last 28 days (GSC_WINDOW_DAYS). */
const DEFAULT_CLICK_DAYS = 28;

/** The one sentence under the title: how much of the site the crawl read,
 *  and how much of it says in code what the business is. Oura's crawler,
 *  not Google's, so it never claims what Google can reach. */
function pageSentence(audit: BusinessAudit, entityMarkup?: { covered: number; applicable: number }): string {
    const n = audit.pagesCrawled;
    const known = audit.pagesKnown;
    const pages = `${n} ${n === 1 ? "page" : "pages"}`;
    let read: string;
    // 0 here is a payload that never sent the count: a crawl that really read
    // nothing is `crawlFailed`, and the screen says so before it gets here.
    if (n === 0) read = "The scan did not record how many pages the crawl read.";
    else if (typeof known === "number" && known > n) read = `The crawl read ${n} of ${known} pages.`;
    else if (audit.truncated) read = `The crawl read ${pages} before it stopped.`;
    else if (known === n) read = `The crawl read all ${pages}.`;
    else read = `The crawl read ${pages}.`;
    return entityMarkup
        ? `${read} ${entityMarkup.covered} of ${entityMarkup.applicable} say in code what the business is.`
        : read;
}

export function SiteAudit({
    snapshot,
    business,
    liveAudit,
    businessId,
    onRefreshData,
    assessmentId,
    onOpenTask,
    onNavigate,
    onOpenPage,
    liveSearch,
    pastScan,
}: {
    snapshot: ScoreSnapshot;
    business: Business;
    /** Real data (ourteam-frontend#3160); the fixture audit otherwise. */
    liveAudit?: BusinessAudit;
    /** Live, writable business id. Present = the fix drawer can generate and
     *  publish a real fix and PageSpeed can be re-scanned; absent on fixtures
     *  and on a past scan, which stays read-only. */
    businessId?: string;
    /** Re-fetch the live payloads after a re-scan or a refresh job lands. */
    onRefreshData?: () => void;
    /** Latest run, writable: the plan join (#4445) and the stage refreshes. */
    assessmentId?: string;
    /** Jump to the Tracker anchored on the task a finding closes. */
    onOpenTask?: (taskId: string) => void;
    /** The Page workshop, Fixes and Connectors, from this screen's links. */
    onNavigate?: (v: View) => void;
    /** Open the Page workshop on one page. */
    onOpenPage?: (page: { url: string; title: string }) => void;
    /** The Search Console block the page clicks come from, for its window. */
    liveSearch?: AeoSearchPerformance;
    /** A past scan is on screen: no refresh control may render. */
    pastScan?: boolean;
}) {
    const audit = liveAudit ?? getAudit(business, snapshot);
    const isStaff = useOuraRole() === "oura-staff";
    const reducedMotion = useMediaQuery(REDUCED_MOTION);

    /**
     * Every role a reviewer gave a page on the Page workshop. The set-aside
     * ones (`product`, `utility`, `placeholder`) are dropped from every
     * finding; the rest name each page's role in the page list.
     *
     * Only the explicit override counts. `placeholder` is never inferred (see
     * pageWorkshop.ts), so this can never hide a page on a guess.
     */
    const [roles, setRoles] = useState(() => rolesByKey(loadStoredRoles(businessId)));
    // Re-seeded whenever the business changes, because the initialiser above
    // runs only once for the component's whole life and this screen is reused
    // across business drill-ins rather than remounted. Without this, walking
    // from one business to another carried the first one's set-aside pages into
    // the second one's findings and hid rows that were never labelled.
    useEffect(() => {
        setRoles(rolesByKey(loadStoredRoles(businessId)));
    }, [businessId]);
    useEffect(() => {
        if (!businessId) return;
        let cancelled = false;
        void fetchStoredRoles(businessId, business.domain).then((server) => {
            if (cancelled) return;
            // Server wins, same as the Page workshop: it is the shared record.
            //
            // Skipped when the server returns nothing, because `fetchStoredRoles`
            // spells "this business has no overrides" and "the request failed"
            // the same way. Treating that as the answer would drop every label
            // the reviewer can see in the workshop the moment the network
            // hiccuped.
            if (Object.keys(server).length > 0) setRoles(rolesByKey(server));
        });
        return () => {
            cancelled = true;
        };
    }, [businessId, business.domain]);

    const setAside = useMemo(() => setAsideKeys(roles), [roles]);
    const scoped = useMemo(() => scopeIssues(audit.issues, setAside), [audit.issues, setAside]);
    const active = useMemo(() => activeIssues(scoped), [scoped]);

    /**
     * The open drawer is held as a KEY, not as the issue object.
     *
     * The scoped list is rebuilt when the role overrides arrive from the
     * server, so a stored object would be a snapshot taken before that: a
     * drawer opened between first paint and the roles landing would keep
     * showing the pages the reviewer had already set aside.
     */
    const [fixIssueKey, setFixIssueKey] = useState<string | null>(null);
    const fixIssue = fixIssueKey ? scoped.find((i) => i.key === fixIssueKey) ?? null : null;

    /**
     * The plan's task list, so a finding can name the step that closes it
     * (#4445). Read-only on purpose: `getTracker` would GENERATE a four-week
     * plan just because somebody opened this screen, which is exactly the
     * mistake the summary route exists to prevent.
     *
     * An audit check is the parent of all three surfaces: it raises the issue
     * row here, the Recommendation, and the tracker task that carries the same
     * key in `source.ref`. That shared key is the whole join.
     */
    const [trackerTasks, setTrackerTasks] = useState<AeoTrackerSummaryTask[] | null>(null);
    useEffect(() => {
        if (!assessmentId) {
            setTrackerTasks(null);
            return;
        }
        let cancelled = false;
        void getTrackerSummary(assessmentId).then((summary) => {
            if (!cancelled) setTrackerTasks(summary?.exists ? summary.tasks : null);
        });
        return () => {
            cancelled = true;
        };
    }, [assessmentId]);

    const taskByCheck = useMemo(() => {
        const map = new Map<string, AeoTrackerSummaryTask>();
        for (const t of trackerTasks ?? []) {
            if (t.source?.type === "check" && t.source.ref && !map.has(t.source.ref)) {
                map.set(t.source.ref, t);
            }
        }
        return map;
    }, [trackerTasks]);

    /**
     * `?issue=<check key>`: arrive with one finding's drawer already open.
     *
     * The key is the audit check key, which is the same key the tracker task
     * carries in `source.ref` and the same key the Recommendation's id is built
     * from, so one identifier reaches all three surfaces.
     *
     * Handled once (a ref, not a state reset) so closing the drawer does not
     * immediately reopen it from the still-present URL param, and the param is
     * cleared so a reload does not resurrect a drawer the user shut.
     *
     * Matched against the findings on screen, not every check: the backend
     * sends the checks that found nothing too, and a link from Fixes to a
     * finding since fixed would open a drawer about zero pages. A miss says
     * so, rather than landing on the screen with nothing happening.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const snackbar = useSnackbar();
    const issueAnchorHandled = useRef(false);
    useEffect(() => {
        if (issueAnchorHandled.current) return;
        const key = searchParams.get("issue");
        if (!key) return;
        issueAnchorHandled.current = true;
        const match = active.find((i) => i.key === key);
        // The app's snackbar has a success and an error tone only; this is
        // neither a failure nor an alarm, so it takes the calm one.
        if (match) setFixIssueKey(match.key);
        else snackbar.showSuccess("That finding is not on this scan.", "It was fixed or set aside.");
        const next = new URLSearchParams(searchParams);
        next.delete("issue");
        setSearchParams(next, { replace: true });
    }, [searchParams, active, setSearchParams, snackbar]);

    // Explicit `=== "true"`, absent means OFF, the rule every Oura flag
    // follows: the routes 404 until ourteam-backend#4995 is deployed, and a
    // control that can only fail is worse than no control.
    const readabilityRefreshLive = import.meta.env.VITE_ENABLE_OURA_READABILITY_REFRESH === "true";
    /**
     * Refreshing AI Readability without a scan (#3831, ourteam-backend#4995).
     * The score is a pure function of the crawl, so an operator who has just
     * added prices should not have to buy a whole assessment to see them
     * counted. Same storage key as it had on the Overview, so a job started
     * there resumes here.
     */
    const readabilityJob = useRefreshJob<AeoReadabilityBlock>({
        storageKey: `oura.readabilityRefresh.${business.id}`,
        start: async () => {
            const r = await startReadabilityRefresh(business.id);
            if (!r.ok) return r;
            return { ok: true, jobId: r.jobId, mode: r.mode, result: r.aiReadability ?? null };
        },
        poll: async (jobId) => {
            const job = await getReadabilityRefresh(business.id, jobId);
            if (job === "gone" || !job) return job;
            return { status: job.status, mode: job.mode, error: job.error, result: job.aiReadability ?? null };
        },
        // Re-read the business so the rest of the page agrees with the block
        // just written, instead of one card living on newer data.
        onSettled: () => onRefreshData?.(),
    });
    /** One crawl, every card that reads it (#3905): one in-flight state and
     *  one outcome, not a spinner per card over the same crawl. */
    const crawlJob = useStageRefresh({
        spec: CRAWL_REFRESH,
        assessmentId,
        businessId: business.id,
        onSettled: () => onRefreshData?.(),
    });
    /** The free recompute over stored answers (#3908). */
    const analyseJob = useStageRefresh({
        spec: ANALYSE_REFRESH,
        assessmentId,
        businessId: business.id,
        onSettled: () => onRefreshData?.(),
    });
    /** Staff only, and only where the route exists and the scan is writable. */
    const canRefresh = (j: { absent: boolean }) => STAGE_RERUN_LIVE && isStaff && !!assessmentId && !j.absent;
    const canRefreshReadability = isStaff && readabilityRefreshLive && !readabilityJob.absent && !pastScan;

    /**
     * The readability block, preferring whatever was measured most recently:
     * the refresh's own result, then the block a crawl re-run carries in the
     * job itself (`results.crawl.aiReadability`, reported as each stage lands),
     * then the stored one. Reading the job's block also keeps the card honest
     * when the stored block disagrees with the job that just wrote it
     * (ourteam-backend#5064).
     */
    const crawlReadability = (crawlJob.blocks?.crawl?.aiReadability ?? null) as AeoReadabilityBlock | null;
    const refreshed = readabilityJob.result ?? crawlReadability;
    const readabilityScore = refreshed
        ? refreshed.score
        : typeof audit.aiReadabilityScore === "number" ? audit.aiReadabilityScore : null;
    const readabilityVerdict = refreshed ? refreshed.verdict ?? null : audit.aiReadabilityVerdict ?? null;
    const readabilityAbsent = refreshed ? refreshed.reason ?? null : audit.aiReadabilityAbsent ?? null;
    const readabilityMeasuredAt = refreshed?.measuredAt ?? audit.aiReadabilityMeasuredAt ?? null;
    const readabilityComponents = refreshed?.components ?? audit.aiReadabilityComponents ?? [];
    /**
     * What the crawl behind the score found out about itself. "Too few pages
     * could be read" leaves an operator unable to tell 2 readable pages of 30
     * from 20 of 30, so the floor travels with the counts.
     */
    const readabilityCrawl = refreshed ? refreshed.crawl ?? null : audit.aiReadabilityCrawl ?? null;
    const readabilityCrawlLine = (() => {
        if (!readabilityCrawl?.pagesCrawled) return null;
        const { pagesCrawled, readablePages, readableShare, readableFloor } = readabilityCrawl;
        const share = typeof readableShare === "number" ? ` (${Math.round(readableShare * 100)}%)` : "";
        const floor = typeof readableFloor === "number" ? ` Scoring needs ${Math.round(readableFloor * 100)}%.` : "";
        return `${readablePages} of ${pagesCrawled} pages could be read${share}.${floor}`;
    })();
    const readabilityAbsentText = (readabilityAbsent && READABILITY_ABSENT_TEXT[readabilityAbsent])
        || (canRefreshReadability
            ? "This scan ran before the score existed. Refresh reads the site and fills it in, with no questions and no engines."
            : "This scan ran before the score existed. The next scan will carry it.");

    // `siteHealthScore` is typed as a number, but the backend sends null with
    // a named reason when the crawl produced nothing to score (#4096).
    const siteHealth = typeof audit.siteHealthScore === "number" ? audit.siteHealthScore : null;
    /** The crawl did not read the site: nothing it "found" is a measurement. */
    const crawlUnread = audit.crawlFailed === true || !!audit.siteHealthScoreAbsent;
    const crawlDay = dayMonth(audit.crawledAt);
    const unreadText = crawlUnread
        ? audit.crawlFailedReason
            || (audit.siteHealthScoreAbsent && SITE_HEALTH_ABSENT_TEXT[audit.siteHealthScoreAbsent])
            || "The crawl could not read this site on this scan."
        : null;
    const sentence = crawlUnread
        ? (audit.siteHealthScoreAbsent && SITE_HEALTH_ABSENT_TEXT[audit.siteHealthScoreAbsent]) || "The crawl could not read this site on this scan."
        : pageSentence(audit, readabilityComponents.find((c) => c.key === "entity_markup"));

    const [strategy, setStrategy] = useState<PageSpeedRun["strategy"]>(() => defaultStrategy(audit.pageSpeed.runs));
    const openCard = (anchor: string) =>
        document.getElementById(anchor)?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });

    /** The audit check behind a readability component that found something,
     *  so "How to fix" opens the real finding and never an empty drawer. */
    const checkFor = (key: (typeof READABILITY_ROWS)[number]["key"]) =>
        READABILITY_ROWS.find((r) => r.key === key)?.checks.find((c) => active.some((i) => i.key === c && i.count > 0)) ?? null;

    const pages = useMemo(
        () => sitePageRows({ priority: audit.priorityPages, crawled: audit.crawledPages, issues: scoped, roles, setAside }),
        [audit.priorityPages, audit.crawledPages, scoped, roles, setAside],
    );

    const strengthsCard = (
        <StrengthsWeaknessesCard
            strengths={audit.strengths}
            weaknesses={audit.weaknesses}
            unread={crawlUnread}
            busy={analyseJob.running || crawlJob.running}
            refresh={canRefresh(analyseJob) ? <CardRefresh job={analyseJob} spec={ANALYSE_REFRESH} /> : undefined}
            progress={<RefreshProgress job={analyseJob} runningText={ANALYSE_REFRESH.running} />}
        />
    );

    return (
        <FoldScope value="staff-site">
            <PageSentence>{sentence}</PageSentence>
            <div className="space-y-5">
                <div aria-busy={crawlJob.running} className={cn("transition-opacity", crawlJob.running && "opacity-50")}>
                    <SiteTiles
                        siteHealth={siteHealth}
                        siteHealthAbsent="Not measured on this scan."
                        previousSiteHealth={audit.previousSiteHealth}
                        readabilityScore={readabilityScore}
                        readabilityComponents={readabilityComponents}
                        mobile={audit.pageSpeed.runs.find((r) => r.strategy === "mobile")}
                        desktop={audit.pageSpeed.runs.find((r) => r.strategy === "desktop")}
                        // The findings on screen, after out-of-scope pages:
                        // the same count the Issues tab states.
                        errors={pagesWith(active, "error")}
                        warnings={pagesWith(active, "warning")}
                        pagesCrawled={audit.pagesCrawled}
                        crawlUnread={crawlUnread}
                        crawledAt={audit.crawledAt}
                        onOpen={openCard}
                        onOpenPhone={() => {
                            setStrategy("mobile");
                            openCard("core-web-vitals");
                        }}
                    />
                </div>

                <CoreWebVitalsCard
                    pageSpeed={audit.pageSpeed}
                    domain={business.domain}
                    businessId={businessId}
                    onRefreshData={onRefreshData}
                    strategy={strategy}
                    onStrategyChange={setStrategy}
                />

                {/* Side by side only from 1280px: at 1024 each card is too
                    narrow for a finding's name and its page count. */}
                <Bento layout="c2xl">
                    <SiteIssuesCard
                        issues={active}
                        checksRun={scoped.length}
                        onFix={setFixIssueKey}
                        pages={pages}
                        hasClicks={audit.priorityPages.some((p) => p.visits > 0)}
                        clickDays={liveSearch?.window?.days ?? DEFAULT_CLICK_DAYS}
                        pagesCrawled={audit.pagesCrawled}
                        setAsideCount={setAside.size}
                        crawlDay={crawlDay}
                        unreadText={unreadText}
                        // The backend's own verdict ("Strong SEO fundamentals, keep protecting it").
                        health={siteHealth !== null ? { score: siteHealth, verdict: audit.siteHealthVerdict || null } : null}
                        busy={crawlJob.running}
                        refresh={canRefresh(crawlJob) ? <CardRefresh job={crawlJob} spec={CRAWL_REFRESH} label="Re-read the site" /> : undefined}
                        progress={<RefreshProgress job={crawlJob} runningText={CRAWL_REFRESH.running} />}
                        onOpenPage={onOpenPage}
                        onOpenAllPages={onNavigate ? () => onNavigate("pages") : undefined}
                        onConnectSearch={onNavigate ? () => onNavigate("connectors") : undefined}
                    />
                    <MachineReadCard
                        score={readabilityScore}
                        verdict={readabilityVerdict}
                        components={readabilityComponents}
                        absentText={readabilityScore === null ? readabilityAbsentText : null}
                        crawlLine={readabilityCrawlLine}
                        measuredAt={readabilityMeasuredAt}
                        checkFor={checkFor}
                        onFix={setFixIssueKey}
                        // Fixes folded into Delivery, and this went on opening the
                        // orphaned Tracker — a page no rail offers any more (#3989).
                        onOpenFixes={onNavigate ? () => onNavigate(DELIVERY_ON ? "delivery" : "todo") : undefined}
                        busy={readabilityJob.running || crawlJob.running}
                        refresh={canRefreshReadability ? (
                            <RefreshButton
                                job={readabilityJob}
                                title="Re-read the site and recompute this score only. No questions, no engines."
                            />
                        ) : undefined}
                        progress={
                            <RefreshProgress
                                job={readabilityJob}
                                runningText={readabilityJob.mode === "crawl" ? "Reading the site again. This takes about a minute." : undefined}
                            />
                        }
                    />
                </Bento>

                {/* The AI reader test (H38), behind Delivery and the Docs write
                    switch: A3's AI readers item closes on it. */}
                {DELIVERY_WORK_ON && isStaff && !pastScan && (
                    <AiReadersCard businessId={business.id} businessName={business.name} onOpenDelivery={onNavigate ? () => onNavigate("delivery") : undefined} />
                )}

                {/* Health by area only when the crawl ran: on a failed crawl
                    the backend sends 0% and "not found" for areas nobody
                    measured. */}
                {crawlUnread ? (
                    strengthsCard
                ) : (
                    <Bento layout="c3">
                        <HealthByAreaCard
                            healthByArea={audit.healthByArea}
                            pagesCrawled={audit.pagesCrawled}
                            pagesKnown={audit.pagesKnown}
                            truncationReason={audit.truncationReason}
                            crawlDay={crawlDay}
                            busy={crawlJob.running}
                        />
                        <Span2>{strengthsCard}</Span2>
                    </Bento>
                )}
            </div>

            {/* Confirm before it runs. A crawl-mode refresh re-reads the whole
                site and overwrites the stored readability block, and the dialog
                is where the operator learns what does NOT move. */}
            <RefreshConfirmDialog job={readabilityJob} title="Refresh AI Readability?" cards={["AI Readability", "What a machine can read"]}>
                <p className="my-0">
                    This reads {business.domain} again and recomputes this score from the pages it finds.
                    It takes about a minute, and it replaces the five numbers in What a machine can read with what the site says today.
                </p>
                <p className="my-0">
                    Nothing else changes: no AI engines are asked, no questions are run, and the
                    AEO Score, Site Health and the last scan date stay as they are.
                </p>
            </RefreshConfirmDialog>

            <IssueFixDrawer
                key={fixIssue?.key ?? "none"}
                issue={fixIssue}
                businessId={businessId}
                // #3514: a status set in the drawer is re-read from the server,
                // or it reverts the moment the drawer closes.
                onRefreshData={onRefreshData}
                trackerTask={fixIssue ? taskByCheck.get(fixIssue.key) : undefined}
                onOpenTask={onOpenTask}
                onClose={() => setFixIssueKey(null)}
                deliveryBusinessId={business.id}
            />
        </FoldScope>
    );
}
